import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserPreferences } from '../models/train-info.model';

export const IOH_USER_PREFERENCES_STORAGE_KEY = 'iohUserPreferences';
const DEFAULT_PREFERENCES: UserPreferences = {};

@Injectable({
  providedIn: 'root'
})
export class UserPreferencesService {
  private readonly userPreferencesApi = `${environment.appConfig.api.userPreferencesApi}`;

  private readonly _preferences = signal<UserPreferences | null>(null);
  readonly preferences = this._preferences.asReadonly();

  private readonly _error = signal<string | null>(null);
  readonly error = computed(() => this._error());

  constructor(private http: HttpClient) {}

  getUserPreferences(): Observable<UserPreferences> {
    return this.http.get<UserPreferences>(this.userPreferencesApi,{ headers: this.getHeaders()}).pipe(
      tap(preferences => {
        this._preferences.set(preferences);
        this.saveToStorage(preferences);
      }),
      catchError(error => this.handleError(error))
    );
  }

  //fetches user preferences once, falling back to defaults
  initializeUserPreferences(): Observable<UserPreferences> {
    const cached = this.loadFromStorage();
    if (cached) {
      this._preferences.set(cached);
      return of(cached);
    }

    return this.getUserPreferences().pipe(
      catchError(error => {
        if (error?.status === 404) {
          return this.saveDefaultUserPreferences().pipe(
            catchError(() => of(DEFAULT_PREFERENCES))
          );
        }
        return of(DEFAULT_PREFERENCES);
      })
    );
  }

  saveDefaultUserPreferences(): Observable<UserPreferences> {
    return this.http
      .put<UserPreferences>(this.userPreferencesApi, DEFAULT_PREFERENCES, {
        headers: this.getHeaders()
      })
      .pipe(
        tap(() => this.resetToDefault()),
        catchError(error => this.handleError(error))
      );
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Application-ID': 'ioh',
      'Content-Type': 'application/json'
    });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessage =
      error?.status === 0
        ? `An error occurred: ${error?.error}`
        : `Backend returned code ${error.status}, body was: ${error?.error}`;
    this._error.set(errorMessage);
    console.error('Failed to load/update user preferences:', errorMessage);
    return throwError(() => error);
  }

  saveFilters(updates: Partial<UserPreferences>): Observable<UserPreferences> {
    return this.http
      .put<UserPreferences>(this.userPreferencesApi, updates, {
        headers: this.getHeaders()
      })
      .pipe(
        switchMap(() => this.getUserPreferences()),
        catchError(error => this.handleError(error))
      );
  }

  //returns cached preferences, falling back to local storage when not yet fetched
  loadFromStorage(): UserPreferences | null {
    return this._preferences() || this.readFromStorage();
  }

  private readFromStorage(): UserPreferences | null {
    try {
      const raw = localStorage.getItem(IOH_USER_PREFERENCES_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as UserPreferences) : null;
    } catch {
      return null;
    }
  }

  private saveToStorage(preferences: UserPreferences): void {
    localStorage.setItem(
      IOH_USER_PREFERENCES_STORAGE_KEY,
      JSON.stringify(preferences)
    );
  }

  resetToDefault(): void {
    this._preferences.set(DEFAULT_PREFERENCES);
    this.saveToStorage(DEFAULT_PREFERENCES);
  }
}




